export enum MissionStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum FeeType {
  DAY = 'DAY',
  HOUR = 'HOUR',
  MONTH = 'MONTH',
  FIXED = 'FIXED',
}

export enum Currency {
  EUR = 'EUR',
  USD = 'USD',
  GBP = 'GBP',
}

/**
 * Mode de rémunération d'une mission (multi-mission : un consultant peut avoir
 * une mission PAYROLL et une mission REVENUE_SHARE en parallèle). Vide/null =
 * hérite du mode par défaut du consultant.
 * Valeurs BE (renommage) : PAYROLL (ex-PAYFIT), REVENUE_SHARE (ex-FULL_LLP),
 * PARTNER (nouveau).
 */
export type RemunerationMode = 'PAYROLL' | 'REVENUE_SHARE' | 'PARTNER';

/** Libellé métier (FR) d'un régime de rémunération. Source unique de vérité. */
export const REMUNERATION_MODE_LABEL: Record<RemunerationMode, string> = {
  PAYROLL: 'CDI Portage',
  REVENUE_SHARE: 'Associé LLP',
  PARTNER: 'Associé',
};

/** Société portée par la mission (client final ou société de facturation). */
export interface MissionCompany {
  id: string;
  name: string;
  legalName?: string | null;
  siret?: string | null;
  vatNumber?: string | null;
  address?: string | null;
}

/** Validateur CRA résolu par le BE (contact chez le client de la mission). */
export interface TimesheetValidator {
  contactId?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** Bon de commande / devis associé à une mission (les missions peuvent être renouvelées, plusieurs bons possibles) */
export interface MissionOrderForm {
  id: string;
  name: string;
  url: string;
  uploadedAt: string;
  /**
   * Ce que le bon **vend** (suivora-api#1587).
   *
   * `null` sur les bons déposés avant ces champs : « on ne sait pas », et non « rien vendu ». Poser
   * zéro afficherait « il ne reste rien » sur chaque bon historique — alarmant, et faux.
   *
   * Les demi-journées existent : la quantité est décimale.
   */
  quantityDays?: number | null;
  /** Début de la période couverte. **Bornes incluses** : du 01/07 au 31/10 vend le 1er et le 31. */
  periodStart?: string | null;
  periodEnd?: string | null;
  /** Prix unitaire propre au bon. `null` = celui de la mission s'applique. */
  unitPrice?: number | null;
  /** La référence que le client cite — « SC20250930-4 ». */
  reference?: string | null;
  /**
   * Jours consommés sur la période du bon : les jours de mission d'un CRA **validé**, lus à la même
   * source que la facture. Un CRA soumis ne compte pas — il peut être refusé, et le reste
   * afficherait alors moins que la réalité.
   */
  consumedDays?: number | null;
  /**
   * Ce qu'il reste. ⚠️ **Peut être négatif**, et c'est l'information utile : le dépassement est ce
   * qu'on veut voir venir avant qu'il arrive. Ne pas le borner à zéro à l'affichage.
   */
  remainingDays?: number | null;
  /**
   * Les jours **offerts** par ce bon — l'enveloppe négociée, pas ce qu'il en reste. `null` quand le
   * bon n'offre rien.
   */
  freeDays?: number | null;
  /**
   * Ce qu'il **reste à offrir** (suivora-api#1595). Les jours offerts s'imputent au plus tôt : ils
   * s'épuisent sur les premiers jours travaillés, pour que le client voie le geste tout de suite et
   * que la promesse soit tenue même si la mission s'arrête avant son terme.
   *
   * ⚠️ **`null` veut dire « ce bon n'offre rien »**, pas « gratuité épuisée ». Zéro les
   * confondrait, et un `?? 0` afficherait « 0 j à offrir » sur tous les bons qui n'ont jamais rien
   * offert.
   */
  freeDaysRemaining?: number | null;
}

/**
 * Ce qu'une correction de bon peut porter (#2152, suivora-api#1593).
 *
 * ⚠️ **Chaque champ est facultatif, et `null` est une valeur** — celle qui efface. Un champ absent
 * est un champ inchangé : les envoyer tous à chaque enregistrement écraserait ce qu'on n'a pas
 * touché.
 *
 * ⚠️ **La paire quantité + période reste indissociable**, et la gratuité ne dépasse pas les jours
 * vendus : les mêmes règles qu'à la création, refusées par le serveur.
 */
export type OrderFormPatch = {
  quantityDays: number | null;
  freeDays: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  unitPrice: number | null;
  reference: string | null;
};

export interface Mission {
  id: string;
  title: string;
  consultantId: string;
  /** Nom d'affichage dénormalisé renvoyé par l'API (consultant.firstName + lastName) */
  consultantName?: string;
  /** **Client final** : là où le consultant travaille. */
  clientId: string;
  /** Nom d'affichage dénormalisé renvoyé par l'API (client.name) */
  clientName?: string;
  /**
   * **Société de facturation** (ESN, portage) : celle à qui l'on facture.
   * `null` = facturation directe au client final — ce n'est pas une absence de donnée.
   */
  billingClientId?: string | null;
  billingClientName?: string | null;
  /**
   * Sociétés servies **avec la mission**, SIRET compris.
   *
   * Préférées à une jointure sur la liste des clients : celle-ci peut n'être pas chargée, être
   * paginée, ou ne pas contenir une société créée à l'instant. La mission, elle, porte toujours
   * les siennes.
   */
  client?: MissionCompany | null;
  billingClient?: MissionCompany | null;
  startDate: string;
  endDate?: string | null;
  estimatedEndDate?: string | null;
  feeType: FeeType;
  rate: number;
  initialDuration?: number | null; // en mois
  paymentDelay?: number | null; // en jours
  /** Échéance calculée en fin de mois (option « 45 jours fin de mois »). */
  paymentTermEndOfMonth?: boolean;
  /**
   * **L'escompte pour paiement anticipé**, surcharge du défaut client (#1766, suivora-api#1324).
   *
   * Taux en **fraction** (`0.05` = 5 %). La paire l'emporte en bloc : si la mission porte un
   * escompte, c'est son taux **et** son délai — jamais son taux avec le délai du client.
   */
  earlyPaymentDiscountRate?: number | null;
  earlyPaymentDelayDays?: number | null;
  currency: Currency;
  bankAccountId?: string | null;
  /** Ce que vaut une journée déclarée, sur une mission au taux horaire. `null` : le défaut de
   *  l'organisation s'applique — 7 heures à défaut. */
  dailyHours?: number | null;
  bankAccountName?: string | null;
  usesTimesheet: boolean; // CRA
  /**
   * @deprecated Ne branche rien (#2170, suivora-api#1627). Stocké, servi, accepté — et **aucun
   * modèle de jalon n'existe au schéma** : ni date, ni montant, ni échéancier. Le serveur l'a
   * déprécié au contrat ; il disparaîtra quand le web et le mobile cesseront de le lire.
   */
  usesFixedMilestones: boolean;
  /** Reconduction tacite : à l'échéance, la mission repart pour la même durée **sauf dénonciation**. */
  tacitRenewal?: boolean;
  /** Dénonciation actée — la mission s'arrêtera à son échéance. `null` = elle se reconduira. */
  renewalDeclinedAt?: string | null;
  renewalDeclineReason?: string | null;
  /**
   * Dernier jour où la dénonciation reste possible — **servi par l'API**, jamais recalculé ici.
   * Le préavis est une règle contractuelle (suivora-api#589) : la recopier côté web ferait
   * diverger deux vérités le jour où il changerait. `null` hors reconduction tacite.
   */
  renewalDeclineDeadline?: string | null;
  /** Échéance qu'aurait la mission après reconduction. `null` si elle ne se reconduira pas. */
  nextRenewalEndDate?: string | null;
  status: MissionStatus;
  location?: string | null; // Lieu de la mission (adresse)
  // Multi-mission : mode de rémunération propre à la mission (null = hérite du consultant)
  remunerationMode?: RemunerationMode | null;
  // Validateur CRA de la mission (remplace progressivement le champ du profil consultant)
  timesheetValidatorName?: string | null;
  timesheetValidatorEmail?: string | null;
  /** Contact validateur résolu/créé par le BE chez le client de la mission (lecture seule). */
  timesheetValidator?: TimesheetValidator | null;
  /**
   * Le contact qui reçoit les factures de **cette** mission (suivora-api#1245) — `null` quand elle
   * s'en remet au destinataire du client.
   *
   * Troisième rôle, distinct des deux autres : le commercial signe, le chef de projet valide, la
   * comptabilité paie.
   */
  billingContactId?: string | null;
  /**
   * **Le même contact, tel que le serveur le sert** (#2426).
   *
   * ⚠️ La réponse ne porte **pas** de `billingContactId` : elle porte cet objet. Lire le champ plat
   * laissait le sélecteur vide sur une mission qui portait un contact — et la modale envoyant tout
   * son formulaire, une modification quelconque l'effaçait en base.
   *
   * Le champ plat reste au contrat : c'est celui qu'on **envoie**.
   */
  billingContact?: TimesheetValidator | null;
  // Spécificités
  /**
   * Nom en clair du signataire client. **Toujours servi**, y compris quand la fiche est rattachée.
   *
   * C'est le seul porteur sur toutes les missions antérieures à suivora-api#969 : l'écran l'affiche
   * donc quoi qu'il arrive, et n'attend jamais le contact pour montrer qui signe.
   */
  clientManager?: string | null;
  /**
   * Le signataire **rattaché au répertoire** (suivora-api#969) — même forme que le validateur CRA.
   *
   * `null` tant qu'aucun contact n'a été rattaché. Le lien n'est **jamais déduit du nom** : deux
   * homonymes chez un même client mèneraient à la mauvaise fiche, sans que rien ne le signale.
   */
  clientManagerContact?: TimesheetValidator | null;
  isTrainingMission: boolean;
  /**
   * « Cette mission a un taux de frais de gestion qui lui est propre. »
   *
   * Le taux lui-même est dans `managementFeeRate` : le booléen a longtemps été cochable **sans que
   * rien ne puisse le porter** (suivora-api#871). Les deux vont désormais ensemble.
   */
  specificManagementFeeRate: boolean;
  /**
   * Taux (%) de frais de gestion propre à la mission, en portage salarial (suivora-api#871).
   *
   * **Prime sur tout le reste** — le taux du salarié porté, puis celui de la société :
   * `mission ?? consultant ?? organisation`. `null` ⇒ on retombe sur eux.
   *
   * Les frais se calculent mission par mission, et c'est ce qui justifie ce niveau : un salarié
   * porté peut tenir deux missions le même mois, négociées à des taux différents.
   */
  managementFeeRate?: number | null;
  /**
   * « Le salarié porté a apporté ce client. » Ouvre son indemnité d'apport d'affaires
   * (suivora-api#965, #967).
   *
   * **C'est la condition nécessaire** : sans elle, aucun taux ne produit d'indemnité, quel que soit
   * le barème du cabinet.
   *
   * **Aucune rétroactivité.** Cocher après coup n'ouvre le droit qu'au premier mois non arrêté —
   * un compte validé n'est jamais réécrit. À dire au moment de cocher, pas après.
   */
  broughtByConsultant?: boolean;
  /**
   * Taux (%) de l'indemnité d'apport propre à cette mission. `null` ⇒ celui du cabinet s'applique.
   *
   * `null` vaut « aucun barème fixé », `0` vaut « zéro pour cent » : deux valeurs distinctes, et
   * elles le restent — même règle que `managementFeeRate`.
   */
  businessContributionRate?: number | null;
  /**
   * Fin du droit à l'indemnité, si l'accord du cabinet la borne. `null` ⇒ toute la durée de la
   * mission : c'est un droit du salarié sur son client, pas un contrat commercial à durée
   * déterminée.
   *
   * Comparée **au mois**, jamais au jour : le compte d'activité est mensuel, et couper au milieu
   * donnerait un prorata que rien d'autre n'y pratique.
   */
  businessContributionEndDate?: string | null;
  timesheetValidatedByClient: boolean;
  refundableNdf: boolean;
  timesheetJustificationRequired: boolean;
  generateTimesheetPdfAutomatically: boolean;
  // Informations affichées dans la facture
  /**
   * Référence interne de la mission, **générée par l'API** à la création et
   * immuable ensuite (suivora-api#546). Le front la lit, ne l'écrit jamais :
   * une référence qu'on peut modifier ne désigne plus rien de façon stable.
   */
  internalReference?: string | null;
  contractNumber?: string | null;
  createdBy?: string | null; // Nom de l'utilisateur qui a créé le draft
  createdAt: string;
  updatedAt: string;
  /** Bons de commande / devis (le plus récent = dernier renouvellement) */
  orderForms?: MissionOrderForm[];
}

export interface MissionFormData {
  /**
   * Le contact qui reçoit les **factures de cette mission** (suivora-api#1245).
   *
   * Facultatif : `null` laisse s'appliquer le destinataire du client. Il sert les grands comptes
   * qui facturent par entité ou par bon de commande — la même société paie, à des adresses
   * différentes selon la mission.
   *
   * Le contact doit appartenir à la **partie facturée** (`billingClient ?? client`) : facturer un
   * intermédiaire et adresser le courrier au client final l'enverrait chez quelqu'un qui n'a pas à
   * le recevoir.
   */
  billingContactId?: string | null;
  title: string;
  consultantId: string;
  consultantName?: string;
  /** Absent quand la société est créée avec la mission : c'est alors `client` qui porte le nom. */
  clientId?: string;
  clientName?: string;
  /** Société de facturation. Vide = facturation directe au client final. */
  /**
   * La société qui **facture**, quand elle diffère du client final.
   *
   * ⚠️ **`null` la retire** (#2201, suivora-api#1648) — et c'est `null`, jamais l'omission : sur une
   * modification, un champ absent veut dire « inchangé ». Une mission rebasculée en facturation
   * directe gardait sinon son intermédiaire côté serveur, l'écran disant une chose et la base une
   * autre.
   *
   * À la création, absent et `null` reviennent au même : il n'y a rien à retirer.
   */
  billingClientId?: string | null;
  /**
   * Société **créée avec la mission**, quand elle n'existe pas encore (suivora-api#618).
   *
   * Exclusive avec l'identifiant correspondant : envoyer les deux, ou aucun, est refusé — un
   * arbitrage silencieux rattacherait la mission à la mauvaise société, ce qui ne se découvre
   * qu'à la facturation. Seule la raison sociale est requise.
   */
  client?: { name: string };
  billingClient?: { name: string };
  startDate: string;
  endDate?: string;
  estimatedEndDate?: string;
  feeType: FeeType;
  rate: number;
  initialDuration?: number;
  paymentDelay?: number;
  paymentTermEndOfMonth?: boolean;
  /** L'escompte de la mission — la paire, ou rien (#1766). */
  earlyPaymentDiscountRate?: number | null;
  earlyPaymentDelayDays?: number | null;
  currency: Currency;
  /**
   * Le compte d'encaissement **désigné par cette mission** (#2164, suivora-api#1628).
   *
   * ⚠️ **`null` est une valeur** : elle dit « pas de surcharge », et le compte par défaut de
   * l'organisation s'applique — le rang du délai de paiement et de l'escompte. Le champ absent
   * laisserait la désignation en place, ce qui n'est pas la même chose.
   */
  bankAccountId?: string | null;
  /**
   * Ce que vaut une **journée déclarée**, pour une mission au taux horaire (#2170,
   * suivora-api#1627).
   *
   * Le CRA ne déclare que des jours : sans cette valeur, une mission horaire n'a aucune quantité à
   * facturer. Le serveur résout `mission.dailyHours → organization.dailyHours → 7`.
   *
   * ⚠️ N'a de sens que sur `HOUR`. Le poser ailleurs n'aurait aucun effet et laisserait croire à un
   * réglage.
   */
  dailyHours?: number;
  bankAccountName?: string;
  usesTimesheet: boolean;
  /**
   * @deprecated Ne branche rien (#2170) : le formulaire ne le pose plus. Laissé **facultatif** pour
   * que les missions qui le portent puissent être modifiées sans que leur valeur soit écrasée par
   * un défaut — le piège trouvé sur le compte bancaire en #2158.
   */
  usesFixedMilestones?: boolean;
  /** Reconduction tacite (défaut : non). Modifiable tant que le préavis n'est pas passé. */
  tacitRenewal?: boolean;
  status?: MissionStatus;
  location?: string; // Lieu de la mission (adresse)
  createdBy?: string; // Nom de l'utilisateur qui a créé le draft
  /** Mode de rémunération de la mission (vide = hérite du consultant). */
  remunerationMode?: RemunerationMode | '';
  /** Validateur CRA (déplacé du profil consultant vers la mission). */
  timesheetValidatorName?: string;
  timesheetValidatorEmail?: string;
  // Spécificité
  clientManager?: string; // Chargé de clientèle (signataire client — distinct du validateur CRA)
  /**
   * La fiche contact du signataire (suivora-api#969). `null` détache le lien sans effacer le nom.
   *
   * Posé explicitement depuis la liste des contacts du client, jamais déduit du nom saisi.
   */
  clientManagerContactId?: string | null;
  isTrainingMission?: boolean;
  specificManagementFeeRate?: boolean;
  /** Le taux qu'annonce le booléen ci-dessus (suivora-api#871). `null` ⇒ celui du salarié porté. */
  managementFeeRate?: number | null;
  /** « Le salarié porté a apporté ce client » — la condition de son indemnité d'apport. */
  broughtByConsultant?: boolean;
  /** Taux (%) de l'indemnité propre à la mission. `null` ⇒ le barème du cabinet s'applique. */
  businessContributionRate?: number | null;
  /**
   * Fin du droit, en `YYYY-MM-DD`. Vide ⇒ toute la durée de la mission.
   *
   * Le formulaire la tient en chaîne vide quand elle n'est pas renseignée ; c'est `null` qui part
   * à l'API — une chaîne vide n'est pas une date, et la validation la refuserait.
   */
  businessContributionEndDate?: string | null;
  timesheetValidatedByClient?: boolean;
  refundableNdf?: boolean;
  timesheetJustificationRequired?: boolean;
  generateTimesheetPdfAutomatically?: boolean;
  // Informations affichées dans la facture
  /** @deprecated Générée par l'API — jamais envoyée par le formulaire. */
  internalReference?: string;
  contractNumber?: string;
  /** URL du bon de commande / devis après upload (côté API) */
  orderFormUrl?: string;
  /*
    Ce que la saisie apporte au-delà du fichier (suivora-api#1587).

    ⚠️ **La quantité et la période vont ensemble.** L'une sans l'autre ne compte rien — une quantité
    sans période ne sait pas sur quels jours s'imputer, une période sans quantité ne sait pas de quoi
    décompter — et le serveur refuse la paire incomplète en nommant ce qui manque.
  */
  orderFormQuantityDays?: number;
  /**
   * Les jours **offerts** sur ce bon — « 103 vendus, 3 offerts ».
   *
   * Le serveur refuse deux saisies : une gratuité **supérieure aux jours vendus** — elle ne
   * s'épuiserait jamais, chaque facture porterait une remise —, et une gratuité **seule**, sans
   * quantité ni période : elle ne pourrait s'imputer sur rien.
   */
  orderFormFreeDays?: number;
  orderFormPeriodStart?: string;
  orderFormPeriodEnd?: string;
  orderFormUnitPrice?: number;
  orderFormReference?: string;
  /** Nom du fichier pour affichage */
  orderFormFileName?: string;
}

/** Société née de la requête de création de mission, et à quel titre. */
export interface CreatedMissionClient {
  id: string;
  name: string;
  role: 'client' | 'billingClient';
}

export interface ConsultantOption {
  label: string;
  value: string;
}

export interface ClientOption {
  label: string;
  value: string;
}

export interface BankAccountOption {
  label: string;
  value: string;
}

export interface FeeTypeOption {
  label: string;
  value: FeeType;
}

export interface CurrencyOption {
  label: string;
  value: Currency;
}
